import { AnimatedCurrency } from '@hub/components/animated-currency';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import { Typography, useBreakpoints } from '@monorepo/nova-ui';
import { Account, AccountBalance, Program } from '@monorepo/types';
import { MidnightValuesModal } from '../../../../../midnight-values-modal';
import {
  DAILY_LIMIT_CONSTANTS,
  DEFAULT_VALUES,
  OBJECTIVE_ICONS,
  OBJECTIVE_STYLES,
  PROFITABLE_DAYS_CONSTANTS,
} from '../constants/objective-constants';
import { useMidnightModal } from '../hooks/use-midnight-modal';
import { DailyLimitType, ObjectiveData } from '../types';
import { getDailyLimitConfig } from '../utils/daily-limit-calculator';
import {
  getDailyLimitLevel,
  getDailyLimitTooltipData,
} from '../utils/objective-calculations';
import { BaseObjectiveProps, useObjectiveBase } from './base';

interface DailyLimitObjectiveProps extends BaseObjectiveProps {
  account?: Account;
  accountBalance?: AccountBalance;
  program: Program | undefined | null;
}

export const DailyLimitObjective = ({
  account,
  accountBalance,
  program,
}: DailyLimitObjectiveProps): ObjectiveData => {
  const { t, theme, getMainTextStyle } = useObjectiveBase();
  const { isModalOpen, handleOpenModal, handleCloseModal } = useMidnightModal();
  const { isMobile } = useBreakpoints();

  const dailyLimitConfig = getDailyLimitConfig(program);

  const limitType = dailyLimitConfig?.type || DailyLimitType.DailyLoss;

  const currentValue =
    accountBalance && program ? getDailyLimitLevel(accountBalance, program) : 0;

  const tooltipContent =
    !isMobile && accountBalance && program ? (
      <Stack gap={1}>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.opposite,
            lineHeight: theme.typography.body2.lineHeight,
          }}
        >
          {t('objectives.dailyLimit.tooltip', {
            ...getDailyLimitTooltipData(accountBalance, program),
          })}
        </Typography>
      </Stack>
    ) : undefined;

  const primaryLabel = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      <Typography
        component="span"
        variant="body2"
        sx={{ color: theme.palette.text.second }}
      >
        {limitType === DailyLimitType.DailyLoss
          ? t('objectives.dailyLoss.label')
          : t('objectives.dailyPause.label')}
      </Typography>
      {!isMobile && tooltipContent && (
        <Tooltip title={tooltipContent}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              sx={{
                color: theme.palette.primary.main,
              }}
            >
              <SystemIcon
                iconName={OBJECTIVE_ICONS.INFO}
                size={PROFITABLE_DAYS_CONSTANTS.INFO_ICON_SIZE}
                sx={{
                  opacity: 0.6,
                }}
              />
            </IconButton>
          </Box>
        </Tooltip>
      )}
    </Box>
  );

  return {
    icon: (
      <SystemIcon
        iconName={OBJECTIVE_ICONS.CALENDAR}
        size={DEFAULT_VALUES.OBJECTIVE_ICON_SIZE}
      />
    ),
    primaryText: primaryLabel,
    secondaryText: (
      <>
        <Box>
          <AnimatedCurrency
            value={currentValue}
            fontSize={DAILY_LIMIT_CONSTANTS.CURRENCY_FONT_SIZE}
            textColor={
              getMainTextStyle(theme)?.color || theme.palette.text.primary
            }
            compact={true}
            currencySymbol="$"
            showAnimation={true}
            fontStyle="normal"
            showDecimals={false}
            letterSpacing="0.03px"
          />
        </Box>
        <Box
          onClick={handleOpenModal}
          sx={{
            ...OBJECTIVE_STYLES.MIDNIGHT_CONTAINER,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <SystemIcon
            iconName={OBJECTIVE_ICONS.MIDNIGHT}
            size={16}
            sx={{ color: theme.palette.text.secondary }}
          />
          <Typography variant="body2" sx={{ color: theme.palette.text.second }}>
            {t('midnightReset.midnightMark')}
          </Typography>
        </Box>

        {account?.tsAccountId && (
          <MidnightValuesModal
            open={isModalOpen}
            onClose={handleCloseModal}
            tsAccountId={account.tsAccountId}
          />
        )}
      </>
    ),
  };
};
